/**
 * outboxFileStore (web) — la copie durable d'un fichier confié à une file d'envoi.
 *
 * Sur le web, le fichier est un `Blob` derrière une adresse `blob:` (l'enregistreur) ou `data:`
 * (l'appareil photo, `canvas.toDataURL`), qui meurt avec l'onglet. La file le range dans IndexedDB
 * (une base par file : `masteur-<dossier>`) tant qu'il n'est pas parti : un onglet fermé puis rouvert
 * le retrouve, sous une nouvelle adresse. La copie rend une adresse `blob:` courte : la référence
 * persistée ne porte jamais les mégaoctets d'une `data:` (le `localStorage` les refuserait). Sans
 * IndexedDB (navigation privée stricte), la copie échoue et la file le dit à la télémétrie ; l'envoi
 * part quand même.
 */
import { fetchResourceFromURI } from "../../../data/utility/fileFromURI"
import { type OutboxFile } from "./outbox"
import { type OutboxFileStore } from "./outboxFileStore"

export type { OutboxFileStore }

const STORE = "files"

function open(dbName: string): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB is not available"))
            return
        }
        const request = indexedDB.open(dbName, 1)
        request.onupgradeneeded = () => request.result.createObjectStore(STORE)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"))
    })
}

async function run<T>(
    dbName: string,
    mode: IDBTransactionMode,
    act: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
    const db = await open(dbName)
    try {
        return await new Promise<T>((resolve, reject) => {
            const request = act(db.transaction(STORE, mode).objectStore(STORE))
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
        })
    } finally {
        db.close()
    }
}

export function createOutboxFileStore<File extends OutboxFile = OutboxFile>(folder: string): OutboxFileStore<File> {
    const dbName = `masteur-${folder}`
    return {
        keep: async (id, file) => {
            const blob: Blob = await fetchResourceFromURI(file.uri)
            await run(dbName, "readwrite", (store) => store.put(blob, id))
            return file.uri.startsWith("data:") ? { ...file, uri: URL.createObjectURL(blob) } : file
        },
        resolve: async (id, file): Promise<File | null> => {
            const blob = await run<Blob | undefined>(
                dbName,
                "readonly",
                (store) => store.get(id) as IDBRequest<Blob | undefined>
            )
            return blob != null ? { ...file, uri: URL.createObjectURL(blob) } : null
        },
        release: async (id) => {
            await run(dbName, "readwrite", (store) => store.delete(id))
        },
    }
}
